import { Contributor, ContributionItem } from '../types/ui/Settlement';
import {
  fetchContributors,
  fetchContributor,
  createContributor as apiCreateContributor,
  updateContributor as apiUpdateContributor,
  deleteContributor as apiDeleteContributor,
  addContributionItem as apiAddContributionItem,
  removeContributionItem as apiRemoveContributionItem,
} from './SettlementApiClient';

// ── Fetch & Query ────────────────────────────────────────────────

export async function loadContributors(): Promise<Contributor[]> {
  try {
    return await fetchContributors();
  } catch (error) {
    console.error('Failed to load contributors:', error);
    return [];
  }
}

/** Like loadContributors, but a failure is thrown so the screen can say so instead of showing an empty list. */
export async function loadContributorsStrict(): Promise<Contributor[]> {
  return fetchContributors();
}

export function summariseContributors(contributors: Contributor[]) {
  const totalContributed = contributors.reduce((sum, c) => sum + Number(c.total_contributed), 0);
  const settledCount = contributors.filter(c => c.reimbursement_status === 'settled' && Number(c.total_contributed) > 0).length;
  const pendingReimbursements = contributors.filter(c => c.reimbursement_status === 'pending' && Number(c.total_contributed) > 0).length;
  const overdueCount = contributors.filter(c => c.reimbursement_status === 'overdue' && Number(c.total_contributed) > 0).length;
  const centralPot = contributors.reduce((sum, c) =>
    sum + c.items_paid_for.reduce((s, i) => s + Number(i.remaining ?? i.amount), 0), 0);
  return {
    total_contributors: contributors.length,
    total_contributed: totalContributed,
    settled_count: settledCount,
    pending_reimbursements: pendingReimbursements,
    overdue_count: overdueCount,
    central_pot: centralPot,
  };
}

export async function getContributorById(id: string): Promise<Contributor | null> {
  try {
    return await fetchContributor(id);
  } catch (error) {
    console.error(`Failed to load contributor ${id}:`, error);
    return null;
  }
}

// ── Create ────────────────────────────────────────────────────────

export async function createContributor(
  data: Omit<Contributor, 'id' | 'created_at' | 'updated_at'>
): Promise<Contributor> {
  try {
    return await apiCreateContributor(data);
  } catch (error) {
    console.error('Failed to create contributor:', error);
    throw error;
  }
}

// ── Update ────────────────────────────────────────────────────────

export async function updateContributor(
  id: string,
  updates: Partial<Contributor>
): Promise<Contributor> {
  return apiUpdateContributor(id, updates);
}

export async function markAsSettled(contributorId: string): Promise<Contributor | null> {
  return updateContributor(contributorId, {
    reimbursement_status: 'settled',
  });
}

export async function markAsPending(contributorId: string): Promise<Contributor | null> {
  return updateContributor(contributorId, {
    reimbursement_status: 'pending',
  });
}

// ── Archive / Restore ────────────────────────────────────────────────

/** Soft-delete: mark contributor as archived */
export async function archiveContributor(id: string): Promise<Contributor> {
  return updateContributor(id, { archived: true });
}

/** Restore an archived contributor */
export async function restoreContributor(id: string): Promise<Contributor> {
  return updateContributor(id, { archived: false });
}

// ── Delete ────────────────────────────────────────────────────────

/** Permanent delete — only allowed for already-archived contributors */
export async function deleteContributor(id: string): Promise<boolean> {
  try {
    const target = await getContributorById(id);
    if (!target || !target.archived) return false;
    await apiDeleteContributor(id);
    return true;
  } catch (error) {
    console.error(`Failed to delete contributor ${id}:`, error);
    return false;
  }
}

// ── Contribution Items ────────────────────────────────────────────

export async function addContributionItem(
  contributorId: string,
  item: Omit<ContributionItem, 'id'>
): Promise<ContributionItem> {
  try {
    return await apiAddContributionItem(contributorId, item);
  } catch (error) {
    console.error(`Failed to add contribution item for ${contributorId}:`, error);
    throw error;
  }
}

export async function removeContributionItem(
  contributorId: string,
  itemId: string
): Promise<boolean> {
  try {
    await apiRemoveContributionItem(contributorId, itemId);
    return true;
  } catch (error) {
    console.error(`Failed to remove contribution item ${itemId}:`, error);
    return false;
  }
}

// ── Statistics & Queries ──────────────────────────────────────────

export async function getContributorStats() {
  try {
    const contributors = await loadContributors();
    const totalContributed = contributors.reduce((sum, c) => sum + Number(c.total_contributed), 0);
    const settledCount = contributors.filter(c => c.reimbursement_status === 'settled' && Number(c.total_contributed) > 0).length;
    const pendingReimbursements = contributors.filter(c => c.reimbursement_status === 'pending' && Number(c.total_contributed) > 0).length;
    const overdueCount = contributors.filter(c => c.reimbursement_status === 'overdue' && Number(c.total_contributed) > 0).length;
    // Money contributed but not yet spent against any vendor — each item's
    // own `remaining` is already live-derived server-side from payments
    // linked via funding_contribution_item_id (falls back to the full
    // amount for an item nothing's been linked to yet).
    const centralPot = contributors.reduce((sum, c) =>
      sum + c.items_paid_for.reduce((s, i) => s + Number(i.remaining ?? i.amount), 0), 0);

    return {
      total_contributors: contributors.length,
      total_contributed: totalContributed,
      settled_count: settledCount,
      pending_reimbursements: pendingReimbursements,
      overdue_count: overdueCount,
      central_pot: centralPot,
    };
  } catch (error) {
    console.error('Failed to get contributor stats:', error);
    return {
      total_contributors: 0,
      total_contributed: 0,
      settled_count: 0,
      pending_reimbursements: 0,
      overdue_count: 0,
      central_pot: 0,
    };
  }
}

export async function getContributorByName(name: string): Promise<Contributor | null> {
  try {
    const contributors = await loadContributors();
    return contributors.find(c => c.name.toLowerCase() === name.toLowerCase()) || null;
  } catch (error) {
    console.error('Failed to get contributor by name:', error);
    return null;
  }
}
